import { FC, useCallback, useState } from "react";
import { useTimeout } from "usehooks-ts";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { clientEnvModule } from "@/modules/client-env";
import { useAppStore } from "@/store";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import {
  StepTitle,
  StepContent,
  FooterContainer,
  FooterSkipButton,
  FooterBackButton,
} from "@/components/onboarding/components/welcome-wizard";
import { OnboardingStepper } from "@/components/onboarding/components/welcome-wizard/shared";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";

export const CalendarConnectStep: FC<StepRenderProps> = observer(({ onComplete }) => {
  const { store } = useAppStore();
  const [isConnecting, setIsConnecting] = useState(false);

  const googleStore = store.calendar.google;
  const outlookStore = store.calendar.outlook;

  const isOAuthInProgress = store.calendarOnboarding.isOAuthInProgress;
  const isLoading = isConnecting || isOAuthInProgress;
  const hasNextStep = store.onboarding.hasNextStep;
  const [showTryAgain, setShowTryAgain] = useState(false);

  useTimeout(() => setShowTryAgain(true), isLoading && !showTryAgain ? 2000 : null);

  const showBrowserHandoffHint = clientEnvModule.isDesktop() && isLoading && showTryAgain;

  const handleConnectGoogle = useCallback(async () => {
    setIsConnecting(true);

    store.calendarOnboarding.setPhase("oauth-started");

    await googleStore.connect();
    setIsConnecting(false);
  }, [store, googleStore]);

  const handleConnectOutlook = useCallback(async () => {
    setIsConnecting(true);

    store.calendarOnboarding.setPhase("oauth-started");

    await outlookStore.connect();
    setIsConnecting(false);
  }, [store, outlookStore]);

  const handleBack = useCallback(() => {
    setIsConnecting(false);
    setShowTryAgain(false);
    store.calendarOnboarding.setPhase("not-started");
  }, [store]);

  const handleSkip = useCallback(() => {
    trackEvent(TrackedEvent.OnboardingCalendarSetupSkipped, {});
    onComplete();
  }, [onComplete]);

  return (
    <>
      <StepTitle>Share your calendar with Mem Agent</StepTitle>
      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm}>
          Mem Agent can remind you about upcoming meetings. One click joins the meeting and starts
          voice notes.
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphSm}>
          Mem Agent can then add your meeting notes to its understanding of your tasks and projects
          to keep you on track.
        </MDSText>

        <StyledCardGroup>
          {isLoading ? (
            <>
              <StyledCalendarCard data-test-id="calendar-connect-skeleton">
                <MDSSkeleton height={20} width="60%" />
              </StyledCalendarCard>
              <StyledCalendarCard data-test-id="calendar-connect-skeleton">
                <MDSSkeleton height={20} width="60%" />
              </StyledCalendarCard>
            </>
          ) : (
            <>
              <StyledCalendarCard>
                <StyledCardLabel>
                  <MDSIcon kind={MDSIconKind.GoogleCalendar} />
                  <MDSText variant={MDSTextVariant.ParagraphSm}>Connect a Google Calendar</MDSText>
                </StyledCardLabel>
                <MDSButton
                  variant={MDSButtonVariant.Default}
                  onClick={handleConnectGoogle}
                  isDisabled={isConnecting}
                >
                  Connect
                </MDSButton>
              </StyledCalendarCard>
              <StyledCalendarCard>
                <StyledCardLabel>
                  <MDSIcon kind={MDSIconKind.OutlookCalendar} />
                  <MDSText variant={MDSTextVariant.ParagraphSm}>
                    Connect an Outlook Calendar
                  </MDSText>
                </StyledCardLabel>
                <MDSButton
                  variant={MDSButtonVariant.Default}
                  onClick={handleConnectOutlook}
                  isDisabled={isConnecting}
                >
                  Connect
                </MDSButton>
              </StyledCalendarCard>
            </>
          )}
        </StyledCardGroup>

        {showBrowserHandoffHint && (
          <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Secondary}>
            We opened your browser — complete sign-in there to connect your calendar.
          </MDSText>
        )}
      </StepContent>

      <FooterContainer align={showTryAgain ? "space-between" : "flex-end"}>
        <OnboardingStepper />
        {showTryAgain && <FooterBackButton onClick={handleBack}>Try again</FooterBackButton>}
        <FooterSkipButton onClick={handleSkip}>
          {hasNextStep ? "Skip" : "Connect a calendar later"}
        </FooterSkipButton>
      </FooterContainer>
    </>
  );
});

const StyledCardGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
`;

const StyledCalendarCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const StyledCardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;
